import { pb, escapeFilterValue } from '../lib/pocketbase'
import type { Contract, ContractType, ContractStatus, ContractIncentive } from '../types'
import { ensureUserAgency } from '../utils/ensureAgency'
import { handleApiError, throwApiError } from '../utils/apiErrorHandler'
import { logger } from '../utils/logger'
import { guardedUpdate } from '../utils/optimisticLock'
import { autoRecordContractCommission } from '../utils/commissionAutoRecord'

export type ContractPayload = {
  title: string // Required
  contract_type: ContractType // Required
  status: ContractStatus // Required
  client_id: string // Required
  organization_id?: string | null
  deal_id?: string | null
  // Financial
  total_value?: number | null
  guaranteed_value?: number | null
  annual_value?: number | null
  apy?: number | null
  signing_bonus?: number | null
  commission_rate?: number | null
  commission_amount?: number | null
  // Dates
  start_date?: string | null
  end_date?: string | null
  signed_date?: string | null
  // Duration
  term_years?: number | null
  contract_years?: number | null
  option_years?: number | null
  free_agency_year?: number | null
  accrued_seasons?: number | null
  // Cap/ranking
  cash_to_cap_ratio?: number | null
  contract_ranking?: number | null
  contract_ranking_position?: string | null
  // Current year values
  current_year?: number | null
  current_base_salary?: number | null
  current_prorated_bonus?: number | null
  current_per_game_roster_bonus?: number | null
  current_workout_bonus?: number | null
  current_guaranteed_salary?: number | null
  current_cap_number?: number | null
  current_cap_percentage?: number | null
  current_cash_payout?: number | null
  // Contract details
  no_trade_clause?: boolean | null
  performance_bonuses?: any[] | null
  incentives?: ContractIncentive[] | null
  yearly_breakdown?: any[] | null
  payments_received?: any[] | null
  roster_history?: any[] | null
  game_log?: any[] | null
  offseason_attendance?: any[] | null
  workout_days_required?: number | null
  void_years?: number[] | null
  option_dates?: any[] | null
  contract_triggers?: any[] | null
  key_terms?: string | null
  contract_doc_url?: string | null
  source_url?: string | null
  paycheck_weeks?: number | null
  paycheck_cadence?: 'weekly' | 'biweekly' | null
  term_sources?: Record<string, unknown> | null
  // Additional fields
  team_or_brand?: string | null
  league?: string | null
  player?: string | null
  document_id?: string | null
  renewal_reminder_days?: number | null
  expiry_notified?: boolean | null
  is_primary?: boolean | null
  notes?: string | null
  tags?: string[] | null
  agency_id?: string
}


export type PaginatedContractsResult = {
  items: Contract[]
  page: number
  perPage: number
  totalItems: number
  totalPages: number
}

function mapContract(record: Record<string, any>): Contract {
  return {
    id: record.id,
    title: record.title ?? '',
    contract_type: record.contract_type ?? 'other',
    status: record.status ?? 'draft',
    // Relations
    client_id: record.client_id ?? '',
    organization_id: record.organization_id ?? null,
    deal_id: record.deal_id ?? null,
    // Financial
    total_value: record.total_value ?? null,
    guaranteed_value: record.guaranteed_value ?? null,
    annual_value: record.annual_value ?? null,
    apy: record.apy ?? null,
    signing_bonus: record.signing_bonus ?? null,
    commission_rate: record.commission_rate ?? null,
    commission_status: record.commission_status ?? null,
    commission_amount: record.commission_amount ?? null,
    // Dates
    start_date: record.start_date ?? null,
    end_date: record.end_date ?? null,
    signed_date: record.signed_date ?? null,
    // Duration
    term_years: record.term_years ?? null,
    contract_years: record.contract_years ?? null,
    option_years: record.option_years ?? null,
    free_agency_year: record.free_agency_year ?? null,
    accrued_seasons: record.accrued_seasons ?? null,
    // Cap/ranking
    cash_to_cap_ratio: record.cash_to_cap_ratio ?? null,
    contract_ranking: record.contract_ranking ?? null,
    contract_ranking_position: record.contract_ranking_position ?? null,
    // Current year values
    current_year: record.current_year ?? null,
    current_base_salary: record.current_base_salary ?? null,
    current_prorated_bonus: record.current_prorated_bonus ?? null,
    current_per_game_roster_bonus: record.current_per_game_roster_bonus ?? null,
    current_workout_bonus: record.current_workout_bonus ?? null,
    current_guaranteed_salary: record.current_guaranteed_salary ?? null,
    current_cap_number: record.current_cap_number ?? null,
    current_cap_percentage: record.current_cap_percentage ?? null,
    current_cash_payout: record.current_cash_payout ?? null,
    // Contract details
    no_trade_clause: record.no_trade_clause ?? null,
    performance_bonuses: Array.isArray(record.performance_bonuses) ? record.performance_bonuses : null,
    incentives: Array.isArray(record.incentives) ? record.incentives : null,
    yearly_breakdown: Array.isArray(record.yearly_breakdown) ? record.yearly_breakdown : null,
    payments_received: Array.isArray(record.payments_received) ? record.payments_received : null,
    roster_history: Array.isArray(record.roster_history) ? record.roster_history : null,
    game_log: Array.isArray(record.game_log) ? record.game_log : null,
    offseason_attendance: Array.isArray(record.offseason_attendance) ? record.offseason_attendance : null,
    workout_days_required: record.workout_days_required ?? null,
    void_years: Array.isArray(record.void_years) ? record.void_years : null,
    option_dates: Array.isArray(record.option_dates) ? record.option_dates : null,
    contract_triggers: Array.isArray(record.contract_triggers) ? record.contract_triggers : null,
    key_terms: record.key_terms ?? null,
    payment_terms: record.payment_terms && typeof record.payment_terms === 'object' ? record.payment_terms : null,
    term_sources: record.term_sources && typeof record.term_sources === 'object' ? record.term_sources : null,
    // PocketBase stores an empty number as 0 and an empty select as ''.
    paycheck_weeks: record.paycheck_weeks > 0 ? record.paycheck_weeks : null,
    paycheck_cadence: record.paycheck_cadence === 'weekly' || record.paycheck_cadence === 'biweekly' ? record.paycheck_cadence : null,
    contract_doc_url: record.contract_doc_url ?? null,
    source_url: record.source_url ?? null,
    // Additional fields
    team_or_brand: record.team_or_brand ?? null,
    league: record.league ?? null,
    player: record.player ?? null,
    document_id: record.document_id ?? null,
    renewal_reminder_days: record.renewal_reminder_days ?? null,
    expiry_notified: record.expiry_notified ?? null,
    is_primary: record.is_primary ?? false,
    notes: record.notes ?? null,
    tags: Array.isArray(record.tags) ? record.tags : null,
    // Agency/ownership
    agency_id: record.agency_id ?? '',
    created_by: record.created_by ?? null,
    owner_id: record.owner_id ?? null,
    // Timestamps
    created: record.created ?? '',
    updated: record.updated ?? '',
  }
}

export async function listContracts(
  clientId?: string,
  opts?: {
    limit?: number
    /** Only contracts whose end_date falls in [from, to] (ISO strings). The calendar's window. */
    endDateBetween?: { from: string; to: string }
  },
) {
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id
  if (!agencyId) throw new Error('No agency_id found on current user')

  const filterParts: string[] = [
    `agency_id = "${escapeFilterValue(agencyId)}"`,
  ]

  if (clientId) {
    filterParts.push(`client_id = "${escapeFilterValue(clientId)}"`)
  }
  if (opts?.endDateBetween) {
    filterParts.push(`end_date >= "${escapeFilterValue(opts.endDateBetween.from)}"`)
    filterParts.push(`end_date <= "${escapeFilterValue(opts.endDateBetween.to)}"`)
  }

  const options: any = {
    sort: '-start_date',
    expand: 'client_id,deal_id',
    filter: filterParts.join(' && '),
  }

  let data: Record<string, any>[]
  if (opts?.limit) {
    const result = await pb.collection('contracts').getList<Record<string, any>>(1, opts.limit, options)
    data = result.items
  } else {
    data = await pb.collection('contracts').getFullList<Record<string, any>>({ ...options, batch: 500 })
  }
  return data.map(mapContract)
}



export async function listContractsPaginated(
  page: number = 1,
  perPage: number = 25,
  options?: { sort?: string; filter?: string }
): Promise<PaginatedContractsResult> {
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id
  if (!agencyId) throw new Error('No agency_id found on current user')

  const agencyFilter = `agency_id = "${escapeFilterValue(agencyId)}"`
  const combinedFilter = options?.filter
    ? `${agencyFilter} && ${options.filter}`
    : agencyFilter

  const result = await pb.collection('contracts').getList<Record<string, any>>(page, perPage, {
    sort: options?.sort ?? '-start_date',
    filter: combinedFilter,
    expand: 'client_id,deal_id',
  })
  return {
    items: result.items.map(mapContract),
    page: result.page,
    perPage: result.perPage,
    totalItems: result.totalItems,
    totalPages: result.totalPages,
  }
}

export async function getContract(contractId: string) {
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id
  if (!agencyId) throw new Error('No agency_id found on current user')

  const record = await pb.collection('contracts').getOne<Record<string, any>>(contractId, {
    expand: 'client_id,deal_id',
  })
  if (record.agency_id !== agencyId) throw new Error('Not authorized')
  return mapContract(record)
}

/**
 * The contract already written for a deal, or null.
 *
 * Contracts carry deal_id, so this is the link between the pipeline and
 * everything downstream of it — commissions, invoices and transactions all
 * hang off a contract, not off a deal. Used to decide whether a closed deal
 * still needs one, and to make sure it is never offered twice.
 */
export async function getContractForDeal(dealId: string): Promise<Contract | null> {
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id
  if (!agencyId) throw new Error('No agency_id found on current user')

  const rows = await pb.collection('contracts').getList<Record<string, any>>(1, 1, {
    filter: `agency_id = "${escapeFilterValue(agencyId)}" && deal_id = "${escapeFilterValue(dealId)}"`,
    sort: '-created',
  })
  return rows.items.length ? mapContract(rows.items[0]) : null
}

export async function createContract(payload: ContractPayload, ownerId: string) {
  // Get user to check for agency_id
  const user = pb.authStore.model
  
  const createPayload: any = { ...payload }
  
  // Use owner_id (confirmed in contracts schema)
  if (ownerId) {
    createPayload.owner_id = ownerId
  }
  
  // Ensure user has an agency assigned (auto-create if needed)
  try {
    const userAgencyId = await ensureUserAgency()
    createPayload.agency_id = userAgencyId
  } catch {
    // If agency creation fails, try to continue without it (for backward compatibility)
    if (user && (user as any).agency_id) {
      createPayload.agency_id = (user as any).agency_id
    }
  }
  
  // Remove undefined/null/empty values (preserve agency_id — even empty, it's needed for queries)
  Object.keys(createPayload).forEach(key => {
    if (key === 'agency_id') return // Never strip agency_id
    if (createPayload[key] === undefined || createPayload[key] === null || createPayload[key] === '') {
      delete createPayload[key]
    }
  })
  
  try {
    const record = await pb.collection('contracts').create(createPayload)
    const contract = mapContract(record)
    // A contract entered as active in one save earns its commission the same
    // way one activated later does. This block was empty until Sep 2026.
    if (createPayload.status === 'active') {
      autoRecordContractCommission(contract).catch(err => {
        logger.error('Commission auto-record failed for new contract', contract.id, err)
      })
    }
    return contract
  } catch (error: any) {
    throwApiError(error, 'Create contract')
  }
}

export async function updateContract(
  contractId: string,
  payload: Partial<ContractPayload>,
  lastUpdated?: string,
) {
  try {
    // Fetch previous state for automation triggers
    let previousContract: Contract | null = null
    try {
      previousContract = await getContract(contractId)
    } catch {
      // Continue without previous state — automation triggers are best-effort
    }

    const record = await guardedUpdate('contracts', contractId, payload, lastUpdated)
    const updatedContract = mapContract(record)

    // Commission follows earned money. Activation books it; any later change
    // to what the contract earns (roster periods, year statuses, the rate,
    // the guarantee) re-prices the ledger row.
    const activated = payload.status === 'active' && previousContract && previousContract.status !== 'active'
    const earningsTouched = ['roster_history', 'yearly_breakdown', 'commission_rate', 'guaranteed_value', 'incentives', 'game_log']
      .some(k => k in payload)
    if (activated || (updatedContract.status === 'active' && earningsTouched)) {
      autoRecordContractCommission(updatedContract).catch(err => {
        logger.error('Commission reconcile failed for contract', updatedContract.id, err)
      })
    }


    return updatedContract
  } catch (error: any) {
    throwApiError(error, 'Update contract')
  }
}

export async function deleteContract(contractId: string) {
  try {
    // Cascade: delete related incentives first
    try {
      const incentives = await pb.collection('incentives').getFullList({
        filter: `contract_id = "${escapeFilterValue(contractId)}"`,
      })
      for (const inc of incentives) {
        await pb.collection('incentives').delete(inc.id)
      }
    } catch (err) {
      // Incentives collection may not exist or have no records — continue
      logger.warn('Failed to delete contract incentives:', err)
    }

    await pb.collection('contracts').delete(contractId)
  } catch (error: any) {
    throwApiError(error, 'Delete contract')
  }
}

export async function listClientContracts(clientId: string) {
  return listContracts(clientId)
}

export async function setPrimaryContract(contractId: string, clientId: string) {
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id
  if (!agencyId) throw new Error('No agency_id found on current user')

  // Clear is_primary from all contracts for this client first
  const existing = await pb.collection('contracts').getFullList<Record<string, any>>({
    filter: `agency_id = "${escapeFilterValue(agencyId)}" && client_id = "${escapeFilterValue(clientId)}" && is_primary = true`,
    fields: 'id',
  })
  await Promise.all(existing.map(r => pb.collection('contracts').update(r.id, { is_primary: false })))

  const record = await pb.collection('contracts').update(contractId, { is_primary: true })
  return mapContract(record)
}

export async function listDealContracts(dealId: string) {
  
  // Get current user's agency for filtering (security: agency isolation)
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id

  if (!agencyId) {
    return []
  }

  const filter = `agency_id = "${escapeFilterValue(agencyId)}" && deal_id = "${escapeFilterValue(dealId)}"`
  const data = await pb.collection('contracts').getFullList<Record<string, any>>({
    filter,
    sort: '-start_date',
  })
  return data.map(mapContract)
}

// Helper to check if contract is active
export function isContractActive(contract: Contract): boolean {
  if (contract.status !== 'active') return false
  if (!contract.start_date || !contract.end_date) return contract.status === 'active'
  const now = new Date()
  const start = new Date(contract.start_date)
  const end = new Date(contract.end_date)
  return now >= start && now <= end
}

// Helper to check if contract is expired
export function isContractExpired(contract: Contract): boolean {
  if (contract.status === 'expired' || contract.status === 'terminated') return true
  if (!contract.end_date) return false
  const now = new Date()
  const end = new Date(contract.end_date)
  return now > end
}

// ============================================================================
// Player Contract Data from Scraper API (Historical/Market Data)
// ============================================================================

import type { Contract as PlayerContract, ContractComparable, ContractComparablesResponse } from '../types/contracts'

import { SCRAPER_API_URL } from '../lib/config'

/**
 * Fetch a player's current contract from external data sources
 *
 * @param sport - 'nfl', 'nba', or 'mlb'
 * @param playerName - Player's full name
 * @returns PlayerContract if found, null otherwise
 */
export async function fetchPlayerContract(
  sport: string,
  playerName: string
): Promise<PlayerContract | null> {
  
  const encodedName = encodeURIComponent(playerName)
  const league = sport === 'Football' ? 'nfl' : sport.toLowerCase()
  const url = `${SCRAPER_API_URL}/api/contracts/${league}/${encodedName}`

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 30000)

  let data: any
  try {
    const response = await fetch(url, { signal: controller.signal })
    clearTimeout(timeoutId)
    if (!response.ok) {
      if (response.status === 404) return null
      throw new Error(`Contract API error: ${response.status}`)
    }
    data = await response.json()
  } catch (err: any) {
    clearTimeout(timeoutId)
    if (err.name === 'AbortError') {
      console.warn(`[Contracts] Timeout fetching contract for ${playerName}`)
      return null
    }
    throw err
  }

  // Return best exact match (newest contract first)
  if (data.exact && data.exact.length > 0) {
    const best = selectBestContractMatch(data.exact)
    if (best) return mapPlayerContractData(best)
  }

  // Fall back to best similar match
  if (data.similar && data.similar.length > 0) {
    const best = selectBestContractMatch(data.similar)
    if (best) return mapPlayerContractData(best)
  }

  return null
}

/**
 * Fetch comparable contracts for a position from external data sources
 *
 * @param position - Position code (e.g., 'QB', 'WR', 'EDGE')
 * @param filters - Filter criteria
 * @returns Array of comparable contracts
 */
export async function fetchContractComparables(
  position: string,
  filters: {
    sport?: string
    minAav?: number
    maxAav?: number
    minAge?: number
    maxAge?: number
    limit?: number
  } = {}
): Promise<ContractComparable[]> {
  
  const params = new URLSearchParams()

  if (filters.sport) params.append('sport', filters.sport)
  if (filters.minAav !== undefined) params.append('minAav', String(filters.minAav))
  if (filters.maxAav !== undefined) params.append('maxAav', String(filters.maxAav))
  if (filters.minAge !== undefined) params.append('minAge', String(filters.minAge))
  if (filters.maxAge !== undefined) params.append('maxAge', String(filters.maxAge))
  if (filters.limit !== undefined) params.append('limit', String(filters.limit))

  const url = `${SCRAPER_API_URL}/api/contracts/comparables/${position}?${params}`

  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Comparables API error: ${response.status}`)
  }

  const data: ContractComparablesResponse = await response.json()
  return data.comparables
}

/**
 * Map raw player contract data from API to PlayerContract type
 */
function mapPlayerContractData(raw: any): PlayerContract {
  const totalValue = raw.total_value ?? raw.value ?? raw.contract_value ?? null
  const years = raw.years ?? raw.length ?? null

  return {
    playerName: raw.player_name ?? raw.player ?? null,
    position: raw.position ?? null,
    team: raw.team ?? null,
    yearSigned: raw.year_signed ?? raw.year ?? null,
    years,
    totalValue: totalValue != null ? Number(totalValue) : null,
    aav: totalValue != null && years != null && years > 0 ? Number(totalValue) / Number(years) : null,
    guaranteed: raw.guaranteed ?? raw.guaranteed_money ?? null,
    signingBonus: raw.signing_bonus ?? null,
    age: raw.age ?? null
  }
}

function contractSignedYear(raw: any): number {
  return Number(
    raw.year_signed ??
    raw.year ??
    raw.signing_year ??
    raw.start_year ??
    0
  ) || 0
}

function contractTotalValue(raw: any): number {
  return Number(
    raw.total_value ??
    raw.value ??
    raw.contract_value ??
    0
  ) || 0
}

function selectBestContractMatch(rows: any[]): any | null {
  if (!Array.isArray(rows) || rows.length === 0) return null
  return [...rows].sort((a, b) => {
    const yearDiff = contractSignedYear(b) - contractSignedYear(a)
    if (yearDiff !== 0) return yearDiff
    return contractTotalValue(b) - contractTotalValue(a)
  })[0]
}






